import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import FullCalendar from '@fullcalendar/react';
import { Box, Button, ButtonGroup, Card, IconButton, Modal, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { GetTime } from '../../app/AppUtils';
import { deleteTimetableMoment, fetchTimetableComplete, resetTimetableMomentManagementStatus } from '../management-timetables/timetableManagementSlice';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', md: 400 },
    boxShadow: 24,
};

export default function TimetableCalendarDisplay({ handleSelection, timetableMoments, status, timetableId }) {
    const dispatch = useDispatch();
    var calendarRef = React.createRef();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { timetableMomentDelete } = useSelector((state) => state.management.timetable);

    const [calendarApi, setCalendarApi] = useState();
    const [selectedMomentId, setSelectedMomentId] = useState();
    const [title, setTitle] = useState();
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Status callback
    useEffect(() => {
        if (timetableMomentDelete.status === "success") {
            dispatch(resetTimetableMomentManagementStatus());
            dispatch(fetchTimetableComplete(timetableId));
        } else if (timetableMomentDelete.status === "failed") {
            dispatch(resetTimetableMomentManagementStatus());
        };
    }, [dispatch, timetableMomentDelete.status, timetableId]);

    useEffect(() => {
        if (calendarRef !== null) {
            if (status === 'success') {
                setCalendarApi(calendarRef.current.getApi());
            }
        }
    }, [calendarRef, status]);

    useEffect(() => {
        if (calendarApi !== null && status === 'success') {
            setTitle(calendarApi?.view.title);
        }
    }, [calendarApi, status]);

    const nextClick = () => {
        calendarApi.next();
        setTitle(calendarApi.view.title);
    };

    const prevClick = () => {
        calendarApi.prev();
        setTitle(calendarApi.view.title);
    };

    const todayClick = () => {
        calendarApi.today();
        setTitle(calendarApi.view.title);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', typography: 'body1' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Button color="secondary" sx={{ borderRadius: 1, border: 0.5 }} onClick={() => todayClick()}>{resourceFinal[appLanguage].words.TODAY}</Button>
                <Typography sx={{ typography: { xs: "body1", md: "h6" }, textAlign: 'center' }}>{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="secondary" sx={{ borderRadius: 1, border: 0.5 }} onClick={() => prevClick()}>
                        <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton color="secondary" sx={{ borderRadius: 1, border: 0.5 }} onClick={() => nextClick()}>
                        <NavigateNextIcon />
                    </IconButton>
                </Stack>
            </Box>
            <Box sx={{ flex: '1 1 auto' }}>
                <FullCalendar
                    ref={calendarRef}
                    firstDay={1}
                    weekends={true}
                    headerToolbar={false}
                    events={timetableMoments}
                    eventDisplay="block"
                    displayEventEnd={true}
                    plugins={[dayGridPlugin, interactionPlugin, multiMonthPlugin]}
                    initialView="multiMonthYear"
                    locale="nl-NL"
                    height="100%"
                    selectable={true}
                    select={function (info) {
                        handleSelection(info)
                    }}
                    eventContent={(arg) => {
                        const event = arg.event;
                        return (
                            <Tooltip title={`${GetTime(event.start)} - ${GetTime(event.end)}`} placement="top" arrow>
                                <Box sx={{ fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {GetTime(event.start)} - {GetTime(event.end)}
                                </Box>
                            </Tooltip>
                        );
                    }}
                    eventClick={
                        function (arg) {
                            handleOpen();
                            setSelectedMomentId(arg.event.id);
                        }
                    }
                />
            </Box>
            <Modal
                open={open}
                onClose={handleClose}
            >
                <Box sx={style}>
                    <Card sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6">{resourceFinal[appLanguage].messages.DELETE_TIMETABLE_SELECTION}</Typography>
                        <ButtonGroup color="secondary" sx={{ mt: 2 }}>
                            <Button color="error" startIcon={<CloseIcon />} onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                            <Button color="success" endIcon={<CheckIcon />} onClick={() => {
                                dispatch(deleteTimetableMoment(selectedMomentId));
                                handleClose();
                            }}>{resourceFinal[appLanguage].words.CONFIRM}</Button>
                        </ButtonGroup>
                    </Card>
                </Box>
            </Modal>
        </Box>
    );
}